import { z } from "zod";
import { opaqueCursorSchema, opaqueIdSchema } from "./opaque.js";
export const deliveryModeSchema = z.enum(["EMAIL", "SMS", "PHONE", "WEB"]);
export const deliveryProviderStatusSchema = z.enum(["WORKING", "SANDBOX", "UNAVAILABLE"]);
export const deliveryExecutionScopeSchema = z.enum(["LIVE_ALLOWED", "TEST_ONLY", "NONE"]);
export const deliveryProviderEvidenceSchema = z
    .object({
    evidenceId: opaqueIdSchema,
    mode: deliveryModeSchema,
    status: deliveryProviderStatusSchema,
    executionScope: deliveryExecutionScopeSchema,
    checkedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
})
    .strict()
    .superRefine((value, context) => {
    const expected = value.status === "WORKING"
        ? "LIVE_ALLOWED"
        : value.status === "SANDBOX"
            ? "TEST_ONLY"
            : "NONE";
    if (value.executionScope !== expected)
        context.addIssue({
            code: "custom",
            path: ["executionScope"],
            message: `Expected ${expected} for ${value.status} provider evidence.`,
        });
});
export const recipientConsentStateSchema = z.enum(["PENDING", "GRANTED", "REVOKED", "EXPIRED"]);
export const recipientConsentSchema = z
    .object({
    consentId: opaqueIdSchema,
    recipientId: opaqueIdSchema,
    mode: deliveryModeSchema,
    state: recipientConsentStateSchema,
    capturedAt: z.string().datetime().nullable(),
    expiresAt: z.string().datetime().nullable(),
    revokedAt: z.string().datetime().nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.state === "GRANTED" && value.capturedAt === null)
        context.addIssue({ code: "custom", path: ["capturedAt"], message: "Required when granted." });
    if (value.state === "REVOKED" && value.revokedAt === null)
        context.addIssue({ code: "custom", path: ["revokedAt"], message: "Required when revoked." });
});
export const deliveryPreferenceSchema = z
    .object({
    preferenceId: opaqueIdSchema,
    bookId: opaqueIdSchema,
    recipientId: opaqueIdSchema,
    modes: z
        .array(deliveryModeSchema)
        .min(1)
        .max(4)
        .refine((modes) => new Set(modes).size === modes.length, "Delivery modes must be unique."),
    timeZone: z.string().trim().min(1).max(100),
    paused: z.boolean(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const deliveryScheduleStateSchema = z.enum([
    "DRAFT",
    "ELIGIBLE",
    "BLOCKED_CONSENT",
    "BLOCKED_PROVIDER",
    "SCHEDULED",
    "CANCELLED",
]);
export const deliveryEligibilitySchema = z.enum([
    "ELIGIBLE",
    "BLOCKED_NO_CONSENT",
    "BLOCKED_PROVIDER_UNAVAILABLE",
    "BLOCKED_PROVIDER_UNVERIFIED",
    "BLOCKED_PREFERENCE",
]);
export const deliveryScheduleIntentSchema = z
    .object({
    intentId: opaqueIdSchema,
    bookId: opaqueIdSchema,
    recipientId: opaqueIdSchema,
    mode: deliveryModeSchema,
    state: deliveryScheduleStateSchema,
    eligibility: deliveryEligibilitySchema,
    consentId: opaqueIdSchema.nullable(),
    providerEvidenceId: opaqueIdSchema.nullable(),
    notBefore: z.string().datetime(),
    expiresAt: z.string().datetime(),
    createdAt: z.string().datetime(),
})
    .strict()
    .superRefine((value, context) => {
    if (["ELIGIBLE", "SCHEDULED"].includes(value.state) && value.eligibility !== "ELIGIBLE")
        context.addIssue({
            code: "custom",
            path: ["eligibility"],
            message: "Eligible or scheduled delivery requires eligible evidence.",
        });
    if (value.eligibility === "ELIGIBLE" && (!value.consentId || !value.providerEvidenceId))
        context.addIssue({
            code: "custom",
            path: ["eligibility"],
            message: "Eligible delivery requires consent and provider evidence IDs.",
        });
});
export const deliveryReceiptStatusSchema = z.enum([
    "ACCEPTED",
    "DELIVERED",
    "BOUNCED",
    "REJECTED",
    "UNAVAILABLE",
]);
export const deliveryReceiptSchema = z
    .object({
    receiptId: opaqueIdSchema,
    status: deliveryReceiptStatusSchema,
    receivedAt: z.string().datetime(),
})
    .strict();
export const deliveryAttemptStatusSchema = z.enum([
    "QUEUED",
    "SENDING",
    "DELIVERED",
    "RETRY_SCHEDULED",
    "FAILED_CLOSED",
    "CANCELLED",
]);
export const deliveryAttemptSchema = z
    .object({
    attemptId: opaqueIdSchema,
    intentId: opaqueIdSchema,
    sequence: z.number().int().positive().max(10),
    status: deliveryAttemptStatusSchema,
    attemptedAt: z.string().datetime().nullable(),
    nextRetryAt: z.string().datetime().nullable(),
    receipt: deliveryReceiptSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.status === "RETRY_SCHEDULED" && value.nextRetryAt === null)
        context.addIssue({
            code: "custom",
            path: ["nextRetryAt"],
            message: "A retry schedule requires a next retry time.",
        });
    if (value.status === "DELIVERED" && value.receipt?.status !== "DELIVERED")
        context.addIssue({
            code: "custom",
            path: ["receipt"],
            message: "Delivered attempts require a delivered receipt.",
        });
});
export const deliveryAttemptPageSchema = z
    .object({
    items: z.array(deliveryAttemptSchema).max(100),
    page: z
        .object({ nextCursor: opaqueCursorSchema.nullable(), hasMore: z.boolean() })
        .strict()
        .superRefine((value, context) => {
        if (value.hasMore !== (value.nextCursor !== null))
            context.addIssue({
                code: "custom",
                path: ["nextCursor"],
                message: "nextCursor must be present exactly when hasMore is true.",
            });
    }),
})
    .strict();
export const requestDeliveryScheduleCommandSchema = z
    .object({
    recipientId: opaqueIdSchema,
    mode: deliveryModeSchema,
    notBefore: z.string().datetime(),
})
    .strict();
//# sourceMappingURL=delivery.js.map